import { Checkbox, Drawer, FormControl, FormControlLabel, Grid2, InputLabel, MenuItem, Select } from "@mui/material";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useParams } from "react-router-dom";
import { RoleEnum, RoleStringEnum } from "../../_common/enum";
import { DataTypeEnum } from "../../_common/enum/dataType.enum";
import { DataTypeAutoComplete } from "../../_components/autocomplete/dataType.autocomplete";
import { StandardButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import { StandardTextValidator } from "../../_components/textValidator/standardTextValidator";
import { toastr } from "../../_components/toastr/toastr";
import { RESET_PASSWORD_ERROR_MESSAGES, RESET_PASSWORD_HELPER_TEXT, RESET_PASSWORD_VALIDATORS } from "../../_const/security.const";
import { getFormatedDate } from '../../_helpers/dateTime.helper';
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { accountService } from "../../_services/accounts.service";
import { usersService } from "../../_services/users.service";

export const UsersManageDrawer = (params: { isOpen?: boolean, onSave(): void, onClose(): void }) => {

    const resetData = () => ({
        id: "",
        name: "",
        email: "",
        role: "",
        roles: [],
        password: "",
        confirmPassword: "",
        isEnableNotification: true
    });

    const { id } = useParams<{ id: string }>();

    const [data, setData] = useState<any>(resetData);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [passwordChange, setPasswordChange] = useState(false);

    useEffect(() => {
        if (id != null)
            fetchData();
        else
            setData(resetData);
    }, [id]);

    useEffect(() => {
        if (isOpen == false)
            setIsOpen(true);
    }, []);

    function fetchData() {
        if (!id) return;

        setIsLoading(true);
        return usersService
            .get(id)
            .then(
                (json) => {
                    setData((_data: any) => { return { ...json, role: json.roles } });
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        var _data = { ...data };
        _data.roles = _data.id != null ? [] : _data.roles || [];
        _data.roles.push(_data.role || "");

        return accountService
            .submit(_data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    resetData();
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!id) return;
        setIsLoading(true);
        return accountService
            .disable(id)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    const handleAddressChange = (event: any) => {
        setData((_data: any) => ({
            ..._data, address:
            {
                ..._data.address, [event.target.name]: event.target.value
            }
        }));
    };

    return (
        <Drawer
            anchor={"right"}
            variant="persistent"
            open={isOpen}
            ModalProps={{
                keepMounted: true
            }}>
            {accountService.isAdmin &&
                <DrawerHeader name={(!!id) ? "Update User" : "Create User"} onClose={params.onClose} />
            }

            {accountService.isUser &&
                <DrawerHeader name={"Update Agent"} onClose={params.onClose} />
            }
            <Grid2 size={{ xs: 12 }}>
                <ValidatorForm
                    onSubmit={submit}
                    noValidate>

                    <Grid2 container spacing={1} sx={{ "padding": "8px" }}>
                        {accountService.isAdmin &&
                            <>
                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="name"
                                        name="name"
                                        label="Name"
                                        required
                                        fullWidth
                                        validators={['required', 'minStringLength:1']}
                                        errorMessages={['required field', "Field cannot be empty"]}
                                        value={data.name}
                                        onChange={handleChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="email"
                                        name="email"
                                        label="Email"
                                        required
                                        fullWidth
                                        validators={['required', 'isEmail']}
                                        errorMessages={['required field', "Invalid email"]}
                                        value={data.email}
                                        onChange={handleChange}
                                    />
                                </Grid2>

                                {(!data.id) &&
                                    <Grid2 size={{ xs: 12 }}>
                                        <StandardTextValidator
                                            autoComplete="off"
                                            required
                                            fullWidth
                                            label="Initial Password"
                                            name="password"
                                            value={data.password}
                                            disabled={isLoading}
                                            onChange={handleChange}
                                            type="password"
                                            validators={RESET_PASSWORD_VALIDATORS(true)}
                                            errorMessages={RESET_PASSWORD_ERROR_MESSAGES(true)}
                                            helperText={!data.password ? RESET_PASSWORD_HELPER_TEXT : "Password is valid"}
                                        />
                                    </Grid2>
                                }

                                {(!!data.id) &&
                                    <Grid2 size={{ xs: 12 }}>
                                        <FormControlLabel control={<Checkbox checked={passwordChange} onChange={() => setPasswordChange(!passwordChange)} />} label="Change Password" />

                                        {passwordChange === true &&
                                            <StandardTextValidator
                                                autoComplete="off"
                                                fullWidth
                                                label="Password"
                                                name="password"
                                                value={data?.password}
                                                onChange={handleChange}
                                                type="password"
                                                validators={RESET_PASSWORD_VALIDATORS(true)}
                                                errorMessages={RESET_PASSWORD_ERROR_MESSAGES(true)}
                                                helperText={!data?.password ? "Only Update if password change is required. " + RESET_PASSWORD_HELPER_TEXT : "✓ Password is valid"} />
                                        }
                                    </Grid2>
                                }

                                <Grid2 size={{ xs: 12 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>User Role</InputLabel>
                                        <Select
                                            label="User Role"
                                            name="role"
                                            value={data.role}
                                            onChange={handleChange}
                                            variant="outlined">
                                            {Object.keys(RoleStringEnum).map((role) => {
                                                const objectValue = parseInt(role);
                                                if (!isNaN(objectValue)) return null;
                                                return (
                                                    <MenuItem key={role} value={RoleStringEnum[role as keyof typeof RoleEnum]}>
                                                        {(role)}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid2>

                                {(data.role === 'Client') &&
                                    <Grid2 size={{ xs: 12 }}>
                                        <DataTypeAutoComplete
                                            key="clientId"
                                            dataType={DataTypeEnum.Clients}
                                            label="Select Client"
                                            hasParent={false}
                                            value={data.clientId}
                                            name={"clientId"}
                                            onChange={handleChange}
                                            required={true} />
                                    </Grid2>
                                }

                                {(data.role === 'Agent') && <>
                                    <Grid2 size={{ xs: 12 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="isEnableNotification"
                                                    checked={data.isEnableNotification ?? true}
                                                    onChange={(event) =>
                                                        setData((_data: any) => ({ ..._data, isEnableNotification: event.target.checked }))
                                                    }
                                                />
                                            }
                                            label="Enable Notifications"
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12 }}>
                                        <StandardTextValidator
                                            key="phone"
                                            name="phone"
                                            label="Phone"
                                            required={data.role === 'Agent' ? true : false}
                                            fullWidth
                                            validators={['required', 'matchRegexp:^\\+?[0-9]{7,15}$']}
                                            errorMessages={['Enter a valid phone number (7-15 digits, optional +)']}
                                            value={data.phone}
                                            onChange={handleChange}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12 }}>
                                        <StandardTextValidator
                                            key="addressLine1"
                                            name="addressLine1"
                                            label="Address Line 1"
                                            fullWidth
                                            required
                                            validators={['required', 'minStringLength:1']}
                                            errorMessages={['required field', "Field cannot be empty"]}
                                            value={data?.address?.addressLine1}
                                            onChange={handleAddressChange}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12 }}>
                                        <StandardTextValidator
                                            key="addressLine2"
                                            name="addressLine2"
                                            label="Address Line 2"
                                            fullWidth
                                            required
                                            validators={['required', 'minStringLength:1']}
                                            errorMessages={['required field', "Field cannot be empty"]}
                                            value={data?.address?.addressLine2}
                                            onChange={handleAddressChange}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12 }}>
                                        <StandardTextValidator
                                            key="city"
                                            name="city"
                                            label="Town/City"
                                            fullWidth
                                            required
                                            validators={['required', 'minStringLength:1']}
                                            errorMessages={['required field', "Field cannot be empty"]}
                                            value={data?.address?.city}
                                            onChange={handleAddressChange}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12 }}>
                                        <StandardTextValidator
                                            key="eirCode"
                                            name="eirCode"
                                            label="EirCode/PostCode"
                                            fullWidth
                                            required
                                            validators={['required', 'minStringLength:1']}
                                            errorMessages={['required field', "Field cannot be empty"]}
                                            value={data?.address?.eirCode}
                                            onChange={handleAddressChange}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12 }}>
                                        <StandardTextValidator
                                            key="country"
                                            name="country"
                                            label="Country"
                                            fullWidth
                                            required
                                            validators={['required', 'minStringLength:1']}
                                            errorMessages={['required field', "Field cannot be empty"]}
                                            value={data?.address?.country}
                                            onChange={handleAddressChange}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12 }}>
                                        <StandardTextValidator
                                            key="unavailableStart"
                                            name="unavailableStart"
                                            label="Agent Unavailable Period Start"
                                            type="date"
                                            fullWidth
                                            value={data.unavailableStart != null ? getFormatedDate(data.unavailableStart) : ""}
                                            onChange={handleChange}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12 }}>
                                        <StandardTextValidator
                                            key="unavailableEnd"
                                            name="unavailableEnd"
                                            label="Agent Unavailable Period End"
                                            type="date"
                                            fullWidth
                                            value={data.unavailableEnd != null ? getFormatedDate(data.unavailableEnd) : ""}
                                            onChange={handleChange}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12 }}>
                                        <StandardTextValidator
                                            key="xeroAccountNumber"
                                            name="xeroAccountNumber"
                                            label="Xero Account Number"
                                            fullWidth
                                            value={data.xeroAccountNumber}
                                            onChange={handleChange}
                                        />
                                    </Grid2>
                                </>
                                }
                            </>
                        }
                        {accountService.isUser &&
                            <>
                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="unavailableStart"
                                        name="unavailableStart"
                                        label="Agent Unavailable Period Start"
                                        type="date"
                                        fullWidth
                                        value={data.unavailableStart != null ? getFormatedDate(data.unavailableStart) : ""}
                                        onChange={handleChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="unavailableEnd"
                                        name="unavailableEnd"
                                        label="Agent Unavailable Period End"
                                        type="date"
                                        fullWidth
                                        value={data.unavailableEnd != null ? getFormatedDate(data.unavailableEnd) : ""}
                                        onChange={handleChange}
                                    />
                                </Grid2>
                            </>
                        }
                        <Grid2 size={{ xs: 12 }}>
                            <Grid2 container spacing={1}>
                                <Grid2 size={{ xs: !!data.id ? 6 : 12 }}>
                                    <StandardButton
                                        saving={saving}
                                        loading={isLoading}
                                        icon="save"
                                        type="submit">
                                        {(data?.isActive === false) ? "Save and Reactivate" : "Save"}
                                    </StandardButton>
                                </Grid2>
                                {(!!data.id && data?.isActive === true) &&
                                    <Grid2 size={{ xs: 6 }}>
                                        <StandardButton
                                            saving={saving}
                                            loading={isLoading}
                                            icon="block"
                                            variant="error"
                                            onClick={disable}>
                                            Disable
                                        </StandardButton>
                                    </Grid2>
                                }
                            </Grid2>
                        </Grid2>

                    </Grid2>
                </ValidatorForm>
            </Grid2>
        </Drawer>
    );
};